import {
  type LogoutReason,
  pushDataLayerEvent,
  type UserRole
} from './dataLayer'

// OCE #8: GTM maps the User-ID field from a *persistent* dataLayer variable, not
// from a field buried inside individual events. Previously user_id was only sent
// inside sign_up_complete / login, so a returning authenticated session (which
// fires neither) never populated User-ID, and the same person on phone + desktop
// counted as two users. Pushing a bare { user_id } object here sets the dataLayer
// variable GTM reads, so it is present at (and after) container init for every
// authenticated session.

export interface AnalyticsIdentity {
  userId: string
  role: UserRole
}

let lastPushedKey: string | undefined

const keyOf = (identity: AnalyticsIdentity | null) =>
  identity ? `${identity.userId}:${identity.role}` : 'guest'

/**
 * Push the current user to the dataLayer as stable variables for GTM's User-ID
 * mapping. De-duplicated: only pushes when the value actually changes, so route
 * changes and re-renders don't spam the dataLayer. Pass null for a guest.
 * `undefined`, not null: that is what clears a GTM data-model key.
 */
export function pushUserIdentityToDataLayer(
  identity: AnalyticsIdentity | null
): void {
  if (typeof window === 'undefined') {
    return
  }
  const key = keyOf(identity)
  if (key === lastPushedKey) {
    return
  }
  lastPushedKey = key
  try {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(
      identity
        ? {
            user_id: identity.userId,
            user_role: identity.role,
            login_state: 'logged_in'
          }
        : { user_id: undefined, user_role: undefined, login_state: 'guest' }
    )
  } catch {
    // best-effort: never let analytics break the app
  }
}

/** The logout event clears the identity itself; the guest push is not repeated. */
export function pushLogoutToDataLayer(reason: LogoutReason): void {
  if (lastPushedKey === undefined || lastPushedKey === 'guest') {
    return
  }
  lastPushedKey = 'guest'
  pushDataLayerEvent({
    event: 'logout',
    user_id: undefined,
    user_role: undefined,
    login_state: 'guest',
    logout_reason: reason
  })
}

export function resetUserIdentityForTests(): void {
  lastPushedKey = undefined
}
